
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';

// Base type
export interface IFilePreviewDataBase {
  filename: string;
  originalFileContent?: Blob; // Original file data for downloading
}

// Specific types
export type FilePreviewDataPdf = IFilePreviewDataBase & {
  fileType: 'pdf';
  pdfUrl?: string;
  pdfContent?: Blob;
};

export type FilePreviewDataDoc = IFilePreviewDataBase & {
  fileType: 'doc';
  pdfUrl?: string;
  pdfContent?: string;
};

export type FilePreviewDataDocx = IFilePreviewDataBase & {
  fileType: 'docx';
  htmlContent: string;
};

export type FilePreviewDataImage = IFilePreviewDataBase & {
  fileType: 'image';
  imageContent: Blob;
  mimeType: string;
};

export type FilePreviewData = FilePreviewDataPdf | FilePreviewDataDoc | FilePreviewDataDocx | FilePreviewDataImage;

@Component({
  selector: 'lib-file-preview',
  standalone: true,
  imports: [],
  templateUrl: './file-preview.component.html',
  styleUrl: './file-preview.component.scss'
})
export class FilePreviewComponent implements OnInit, OnDestroy {
  @Input() data: FilePreviewData;

  htmlContent: SafeHtml | null = null;
  previewUrl: SafeResourceUrl | null = null;
  errorMessage: string | null = null;
  private blobUrl: string | null = null;

  constructor(private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public modalData: FilePreviewData | null) {
  }

  ngOnInit() {
    this.loadData();

    if (!this.data) {
      this.errorMessage = 'No preview data provided.';
      return;
    }

    try {
      switch (this.data.fileType) {
        case 'pdf':
        case 'doc':
          this.loadPdf();
          break;
        case 'docx':
          this.loadHtml();
          break;
        case 'image':
          this.loadImage();
          break;
        default:
          this.errorMessage = 'Unsupported file type for preview.';
      }
    } catch (error) {
      this.cleanupBlobUrl();
      this.errorMessage = 'Failed to load preview.';
      console.error('Error loading preview:', error);
    }
  }

  private loadData() {
    // Only take over from the input when the injected dialog data is resolved preview data.
    // The modal can also be opened with an async wrapper, which is not renderable here.
    if (this.modalData && 'fileType' in this.modalData) {
      this.data = this.modalData;
    }
  }

  private loadPdf() {
    const { pdfUrl, pdfContent } = this.data as FilePreviewDataPdf;
    if (pdfContent) {
      this.blobUrl = URL.createObjectURL(pdfContent);
      const pdfWithParams = this.addPdfViewerParams(this.blobUrl);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfWithParams);
      return;
    }

    if (pdfUrl) {
      const pdfWithParams = this.addPdfViewerParams(pdfUrl);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfWithParams);
      return;
    }

    this.errorMessage = 'No PDF content available for preview.';
  }

  private loadHtml() {
    this.htmlContent = (this.data as FilePreviewDataDocx).htmlContent;
  }

  private loadImage() {

    const imageContent = (this.data as FilePreviewDataImage).imageContent;
    if (!imageContent) {
      this.errorMessage = 'No image content available for preview.';
      return;
    }

    this.blobUrl = URL.createObjectURL(imageContent);
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
  }

  handleImageError() {
    this.errorMessage = 'Failed to load image preview.';
    this.previewUrl = null;
  }

  private cleanupBlobUrl() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  private addPdfViewerParams(url: string): string {
    // Parameters to hide specific features while keeping navigation
    const params = new URLSearchParams();

    // params.append('view', 'FitV');
    // params.append('toolbar', '0');
    // params.append('navpans', '1');

    // Construct the final URL with fragment identifier
    return `${url}#${params.toString()}`;
  }

  ngOnDestroy() {
    this.cleanupBlobUrl();
  }
}
